import { Vector3 } from '@technology/web3d';
import { Point3D } from '@trimble/utilities';

let lastPoint: Vector3 | null = null;
let lastIndex: number | null = null;
/**
 * Finds the nearest station to a given position
 * @param pos Position to find the nearest station to
 * @param stationPositions Array of station positions
 * @param tolerance Optional maximum distance tolerance. If provided, only stations within this distance are considered
 * @returns Object containing the position of the nearest station and its index, or null if no station is within tolerance
 */
export function findClosestStation(
  pos: Vector3,
  stationPositions: Vector3[],
  tolerance?: number
): {
  position: Point3D;
  index: number;
} | null {
  // Some rudimentary optimization, but helps a lot with sync when
  // the user looks around only.
  if (lastPoint && lastIndex !== null && pos.equals(lastPoint)) {
    return { position: stationPositions[lastIndex], index: lastIndex };
  }

  if (stationPositions.length === 0) {
    return null;
  }

  let minDistance = Number.MAX_VALUE;
  let nearestStationPos: Point3D = stationPositions[0];
  let nearestIndex: number = 0;
  for (let i = 0; i < stationPositions.length; i++) {
    const position = stationPositions[i];
    const squaredDistanceApprox =
      (position.x - pos.x) * (position.x - pos.x) +
      (position.y - pos.y) * (position.y - pos.y);
    if (squaredDistanceApprox < minDistance) {
      minDistance = squaredDistanceApprox;
      nearestStationPos = position;
      nearestIndex = i;
    }
  }

  // If tolerance is specified and the nearest station is beyond tolerance, return null
  // If tolerance is specified and the nearest station is beyond tolerance, return null
  if (tolerance !== undefined && minDistance > tolerance * tolerance) {
    return null;
  }

  lastPoint = pos;
  lastIndex = nearestIndex;
  return { position: nearestStationPos, index: nearestIndex };
}

/**
 * Finds the nearest station to a given position using full 3D distance
 * @param pos Position to find the nearest station to
 * @param stationPositions Array of station positions
 * @returns Object containing the position of the nearest station and its index
 */
export function findClosest3dStation(
  pos: Vector3,
  stationPositions: Vector3[]
): {
  position: Point3D;
  index: number;
  distance: number;
} {
  let minDistance = Number.MAX_VALUE;
  let nearestStationPos: Point3D = stationPositions[0];
  let nearestIndex: number = 0;

  for (let i = 0; i < stationPositions.length; i++) {
    const position = stationPositions[i];
    const distance = pos.distanceTo(position);

    if (distance < minDistance) {
      minDistance = distance;
      nearestStationPos = position;
      nearestIndex = i;
    }
  }

  return {
    position: nearestStationPos,
    index: nearestIndex,
    distance: minDistance,
  };
}
